import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { EmployeePosition, EmployeePositionInput } from '@/types/employees';

export const useEmployeePositions = (brandId: string | null) => {
  const [positions, setPositions] = useState<EmployeePosition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchPositions = useCallback(async () => {
    if (!brandId) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('os_employee_positions')
        .select('*')
        .eq('brand_id', brandId)
        .order('level', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setPositions((data || []) as EmployeePosition[]);
    } catch (error: any) {
      console.error('Error fetching positions:', error);
      toast.error('Failed to load positions');
    } finally {
      setIsLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  const createPosition = async (input: EmployeePositionInput): Promise<EmployeePosition | null> => {
    if (!brandId) return null;
    setIsCreating(true);
    try {
      const { data, error } = await supabase
        .from('os_employee_positions')
        .insert({
          brand_id: brandId,
          name: input.name,
          description: input.description,
          department: input.department,
          level: input.level ?? 1,
          productive_hours_per_day: input.productive_hours_per_day ?? 8,
          is_active: input.is_active ?? true,
        })
        .select()
        .single();

      if (error) throw error;
      toast.success('Position created');
      await fetchPositions();
      return data as EmployeePosition;
    } catch (error: any) {
      console.error('Error creating position:', error);
      toast.error('Failed to create position');
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  const updatePosition = async (id: string, input: Partial<EmployeePositionInput>): Promise<EmployeePosition | null> => {
    setIsUpdating(true);
    try {
      const { data, error } = await supabase
        .from('os_employee_positions')
        .update({
          name: input.name,
          description: input.description,
          department: input.department,
          level: input.level,
          productive_hours_per_day: input.productive_hours_per_day,
          is_active: input.is_active,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      toast.success('Position updated');
      await fetchPositions();
      return data as EmployeePosition;
    } catch (error: any) {
      console.error('Error updating position:', error);
      toast.error('Failed to update position');
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  const deletePosition = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('os_employee_positions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Position deleted');
      await fetchPositions();
      return true;
    } catch (error: any) {
      console.error('Error deleting position:', error);
      toast.error('Failed to delete position');
      return false;
    }
  };

  return {
    positions,
    isLoading,
    isCreating,
    isUpdating,
    refetch: fetchPositions,
    createPosition,
    updatePosition,
    deletePosition,
  };
};
